import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/DateRangePicker';
import { ExportButton } from '@/components/ExportButton';
import { GeoFilters } from '@/components/GeoFilters';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">{p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}</p>
      ))}
    </div>
  );
}

export default function DistributionPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ sku_data: [], kpis: {}, categories: [] });
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (zone) params.zone = zone;
        if (subzone) params.subzone = subzone;
        if (state) params.state = state;
        if (city) params.city = city;
        if (category) params.category = category;
        if (search) params.search = search;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const res = await api.get('/distribution', { params });
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load distribution data');
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [api, zone, subzone, state, city, category, search, dateFrom, dateTo]);

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="distribution-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Distribution</h1>
          <p className="text-sm text-muted-foreground mt-1">SKU coverage and outlet distribution analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <ExportButton module="distribution" params={{ zone, subzone, state, city, category, date_from: dateFrom, date_to: dateTo }} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone} state={state} setState={setState} city={city} setCity={setCity} />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="dist-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{data.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search SKU..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 w-56" data-testid="dist-search" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Outlets</p>
          <p className="text-xl font-heading font-bold mt-1">{k.total_outlets?.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Productive Outlets</p>
          <p className="text-xl font-heading font-bold mt-1">{k.productive_outlets?.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Coverage</p>
          <p className="text-xl font-heading font-bold mt-1">{k.coverage_pct}%</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Active SKUs</p>
          <p className="text-xl font-heading font-bold mt-1">{k.unique_skus}</p>
        </div>
      </div>

      {/* Chart + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="font-heading font-semibold text-sm mb-4">Top SKUs by Distribution</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.sku_data?.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="sku_name" tick={{ fontSize: 8 }} width={140} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="outlet_count" name="Outlets" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5 overflow-auto">
          <h3 className="font-heading font-semibold text-sm mb-4">SKU Distribution Table</h3>
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : (
            <div className="max-h-80 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left text-xs sticky top-0 bg-card">SKU</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Outlets</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Dist %</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Qty</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.sku_data?.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <Package size={12} className="text-muted-foreground flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{row.sku_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{row.outlet_count}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{row.distribution_pct}%</TableCell>
                      <TableCell className="text-right text-sm">{row.total_qty?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-sm">{formatINR(row.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.sku_data || data.sku_data.length === 0) && (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No distribution data</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
